import { createFetch } from '@vueuse/core'
import { destr } from 'destr'

export const createWrappedFetch = (baseOptions: {
  baseUrl: string
  getHeaders: () => Record<string, string>
  // Quando true, mantem o objeto de erro aninhado ({ message, errors })
  // sem achatar para string, permitindo ao caller ler errors.details.
  // Default (false) preserva o comportamento historico de todas as requests.
  preserveErrorDetails?: boolean
}) => {
  const fetchInstance = createFetch({
    baseUrl: baseOptions.baseUrl,
    fetchOptions: {
      headers: baseOptions.getHeaders(),
      credentials: 'include',
    },
    options: {
      refetch: true,

      async beforeFetch({ options }) {
        // Tenta memória primeiro, depois cookie direto
        const { getCsrfToken } = await import('@/composables/useAuthSession')
        let csrfToken = getCsrfToken()

        // Fallback direto do cookie se memória está vazia
        if (!csrfToken) {
          const match = document.cookie.match(/(?:^|; )_host_csrf=([^;]*)/)
          if (match) csrfToken = decodeURIComponent(match[1])
        }

        if (csrfToken) {
          options.headers = {
            ...options.headers,
            'X-Csrf-Token': `${csrfToken}`,
          }
        }

        return { options }
      },

      afterFetch(ctx) {
        const { data, response } = ctx
        let parsedData = null
        try {
          parsedData = destr(data)
        } catch {}
        return { data: parsedData, response }
      },

      onFetchError(ctx) {
        const { data, response } = ctx
        let parsedData = null
        try {
          parsedData = destr(data)
        } catch {}

        // Normaliza o campo message quando a API retorna um objeto aninhado
        // ex: { statusCode: 400, message: { message: "...", error: "...", statusCode: 400 } }
        // Requests que passam preserveErrorDetails mantem o objeto aninhado
        // intacto para poder ler errors.details (ex: erros de validacao Zod).
        if (
          !baseOptions.preserveErrorDetails &&
          parsedData !== null &&
          typeof parsedData === 'object' &&
          'message' in parsedData &&
          typeof parsedData.message === 'object' &&
          parsedData.message !== null &&
          'message' in parsedData.message
        ) {
          // Preserva o `code` do objeto aninhado no nivel de cima antes de
          // achatar `message` para string (ex.: 423 AI_PROCESSING_LOCKED, cujo
          // `code` vem dentro de `message` pelo filtro global do backend).
          // #55985: sem isto o `code` se perde e o 423 nao e detectado.
          const nested = parsedData.message as Record<string, unknown>
          const root = parsedData as Record<string, unknown>

          if (nested.code !== undefined && root.code === undefined)
            root.code = nested.code

          parsedData.message = nested.message
        }

        return { error: parsedData, response }
      },
    },
  })

  return async (url: string, requestInit?: RequestInit) => {
    let fetchResult = await fetchInstance(url, requestInit || {})

    if (
      fetchResult.response?.value?.status === 401 &&
      !url.includes('/oauth2/refresh')
    ) {
      // Usa o lock compartilhado com loadAuthSession para evitar
      // rotação simultânea do refresh token (replay attack).
      const { performRefresh } = await import('@/composables/useAuthSession')
      const { ok: refreshed, serverError } = await performRefresh()

      if (refreshed) {
        fetchResult = await fetchInstance(url, requestInit || {})
      } else if (!serverError) {
        // Só desloga se o backend confirmou que a sessão é inválida (4xx).
        // Erros de rede ou 5xx (backend reiniciando) não devem deslogar.
        const domainBase = import.meta.env.VITE_DOMAIN_BASE
        document.cookie = `_logged_in=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domainBase}`
        window.location.href = '/login'
      }
    }

    return {
      data: fetchResult.data,
      response: fetchResult.response,
      error: fetchResult.error,
    }
  }
}
